import { ENDPOINTS } from 'features/daily-plan/constants';
import { Permissions, userHasPermission } from 'services/Authorization';
import { ReferenceCheckTabLayout } from 'UI/components/organisms/inventoryProfiles';
import JobOrdersTab from 'UI/components/organisms/inventoryProfiles/JobOrdersTab';
import {
  CandidateProfileJobOrderColumns,
  VirtualProps as jobOrderVirtualProps
} from 'UI/components/organisms/inventoryProfiles/JobOrdersTab/candidatesJOcolumns';
import {
  CANDIDATE_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE,
  CANDIDATE_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE,
  MatchMakingTab
} from 'UI/components/organisms/inventoryProfiles/MatchMakingTab';
import PlacementsTab from 'UI/components/organisms/inventoryProfiles/PlacementsTab';
import {
  candidateProfilePlacementsColumns,
  IncludesByMode
} from 'UI/components/organisms/inventoryProfiles/PlacementsTab/columns';
import SendoutsTab from 'UI/components/organisms/inventoryProfiles/SendoutsTab';
import {
  SENDOUTS_COLUMNS,
  SENDOUTS_RENDERERS,
  SENDOUTS_VIRTUAL_PROPS
} from 'UI/components/organisms/inventoryProfiles/SendoutsTab/columns';
import { ProfileTablesPreferenceKeys } from 'UI/components/organisms/inventoryProfiles/tabKeys';
import {
  formatTasksResponse,
  TASK_COLUMNS,
  TASKS_EXTRA_RENDERERS,
  TasksTab
} from 'UI/components/organisms/inventoryProfiles/TasksTab';
import { ResumeViewer } from 'UI/components/organisms/inventoryProfiles/viewers';
import { EnvApiUrls } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import {
  ContactEntity,
  ContactRole,
  ENTITY_TYPE_ID_MAP,
  EntityType,
  JobOrderEntity,
  TabKeys
} from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { filterColumnsByFeatureFlag, hasFeatureFlag } from 'UI/utils';
import { formatResponseBuilder } from 'UI/utils/inventory';

import formatReferenceCheckResponse from './utils/format-reference-check-response';
import { REFERENCE_CHECK_COLUMNS } from './CandidateProfile.constants';

const formatResponse = response => formatReferenceCheckResponse(response);

const JOB_ORDER_KEYWORDS_DEFINITION = [
  {
    entry: 'company_name',
    valuesToAttach: [
      { name: 'id', path: 'company.id' },
      { name: 'name', path: 'company.name' }
    ]
  }
];

const jobOrderColumnsWithFeatureFlag = [
  {
    featureFlag: FeatureFlags.JobOrderColumnsHAProfile,
    columns: ['vRecruiter.user_name', 'last_activity_date', 'whiteSheet.jobOrderType', 'status']
  }
];

const jobOrderColumns = filterColumnsByFeatureFlag(
  CandidateProfileJobOrderColumns,
  jobOrderColumnsWithFeatureFlag
);

const handleJobOrderResponseChange = response =>
  formatResponseBuilder(response)(JOB_ORDER_KEYWORDS_DEFINITION);

const includesDefinition =
  'position,specialty,address,whiteSheet,company,status,lastActivityLog,keywordTags';

const entityTypesForMatchMaking = [
  {
    columns: CANDIDATE_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE[EntityType.Joborder],
    endpoint: Endpoints.CandidateMatchJobOrder,
    formatResponse: CANDIDATE_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE[EntityType.Joborder],
    id: ENTITY_TYPE_ID_MAP.jobOrder,
    isEnabled: true,
    secondaryEnpoint: Endpoints.CandidateAIMatchJobOrder,
    singular: JobOrderEntity.singular,
    tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesMatchJobOrder,
    title: JobOrderEntity.title,
    type: EntityType.Joborder
  },
  {
    columns: CANDIDATE_MATCH_MAKING_COLUMNS_BY_ENTITY_TYPE[EntityType.Contact],
    endpoint: Endpoints.CandidateMatchContact,
    formatResponse: CANDIDATE_MATCH_MAKING_FORMATTERS_BY_ENTITY_TYPE[EntityType.Contact],
    id: ContactRole.Name,
    isEnabled: true,
    singular: ContactEntity.singular,
    tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesMatchContact,
    title: ContactEntity.title,
    type: EntityType.Contact
  }
];

export const candidateTabsDefinition = [
  {
    id: TabKeys.ResumeViewer,
    label: 'Resume',
    Component: ResumeViewer,
    props: {}
  },
  {
    id: TabKeys.MatchMaking,
    label: 'Match Making',
    Component: MatchMakingTab,
    props: {
      availableEntityTypesToMatch: entityTypesForMatchMaking,
      DataTableProps: {
        apiVersion: 1
      }
    }
  },
  {
    id: TabKeys.JobOrders,
    label: 'Job Orders',
    endpoint: Endpoints.JobOrders,
    Component: JobOrdersTab,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesJobOrders,
      DataTableProps: {
        includes: hasFeatureFlag(FeatureFlags.JobOrderColumnsHAProfile)
          ? `${includesDefinition},vRecruiter`
          : includesDefinition,
        apiVersion: 1,
        virtualProps: jobOrderVirtualProps,
        formatResponse: handleJobOrderResponseChange,
        orderByOptions: { column: 'created_at', direction: 'desc' }
      },
      initialColumns: jobOrderColumns
    }
  },
  {
    id: TabKeys.Sendouts,
    label: 'Sendouts',
    Component: SendoutsTab,
    endpoint: Endpoints.Sendouts,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesSendouts,
      initialColumns: SENDOUTS_COLUMNS,
      extraRenderers: SENDOUTS_RENDERERS,
      DataTableProps: {
        apiVersion: 1,
        virtualProps: SENDOUTS_VIRTUAL_PROPS,
        includes:
          'type,status,interviews,hiringAuthorithies,jobOrderAccountable,candidateAccountable,joborder,candidate,jobOrderAccountableCoach,candidateAccountableCoach'
      }
    }
  },
  {
    id: TabKeys.Placements,
    label: 'Placements',
    Component: PlacementsTab,
    visible: () => userHasPermission(Permissions.Placements.CanUseModule),
    endpoint: Endpoints.Placements,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesPlacement,
      initialColumns: candidateProfilePlacementsColumns,
      DataTableProps: {
        includes: IncludesByMode.candidate,
        apiVersion: 2
      }
    }
  },
  {
    id: TabKeys.ReferenceCheck,
    label: 'Reference Checks',
    Component: ReferenceCheckTabLayout,
    props: {
      tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesReferenceChecks,
      initialColumns: REFERENCE_CHECK_COLUMNS,
      DataTableProps: {
        endpoint: `${EnvApiUrls.API_URL_AWS}/${Endpoints.ReferenceCheckByCandidate}`,
        httpHeaders: {
          ...(EnvApiUrls.SEND_API_KEY && { 'x-api-key': EnvApiUrls.API_KEY_AWS })
        },
        formatResponse
      },
      paramsBuilder: [
        {
          name: 'id',
          pathToValue: 'id'
        },
        {
          name: 'cache',
          value: 'false'
        }
      ]
    }
  },
  {
    id: TabKeys.Tasks,
    label: 'Action Plan',
    Component: TasksTab,
    visible: () =>
      userHasPermission(Permissions.DailyPlan.CanUseModule) &&
      hasFeatureFlag(FeatureFlags.DailyPlanAddTaskFromProfile),
    props: {
      DataTableProps: {
        apiVersion: 1,
        endpoint: ENDPOINTS.tasks,
        formatResponse: formatTasksResponse
      },
      entityType: EntityType.Candidate,
      entityTypeId: ENTITY_TYPE_ID_MAP.candidate,
      extraRenderers: TASKS_EXTRA_RENDERERS,
      initialColumns: TASK_COLUMNS,
      tablePreferencesKey: ProfileTablesPreferenceKeys.CandidatesTasks
    }
  }
];
